import { styled } from '@openeducation/oe-rn-themes';
import React, { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, View } from 'react-native';
import { useTheme } from 'react-native-paper';

import { OEText } from '../../../atoms/oe-text/oe-text';
import { bc, borderColor, flex } from '../../../style-utils';

export interface ProgressBarPopupProps {
  delay: number;
  percentage: number;
  barWidth: number;
  isVisible: boolean;
}

export const ProgressBarPopup: React.FC<ProgressBarPopupProps> = ({
  delay,
  percentage,
  barWidth,
  isVisible,
}) => {
  const { colors } = useTheme();
  const { current: animationProgress } = useRef(new Animated.Value(0));

  useEffect(() => {
    Animated.sequence([
      Animated.delay(delay),
      Animated.timing(animationProgress, {
        useNativeDriver: true,
        toValue: isVisible ? 1 : 0,
        duration: 150,
        easing: Easing.out(Easing.linear),
      }),
    ]).start();
  }, [isVisible, animationProgress, delay]);

  const translateY = animationProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [4, 0],
  });

  return (
    <PopupContainer>
      <Animated.View
        style={[
          flex('align-items-center', 'justify-content-center'),
          styles.popup,
          bc(colors.primary),
          {
            transform: [{ translateY }],
            opacity: animationProgress,
            left: barWidth * percentage - 18,
          },
        ]}
      >
        <OEText fontSize={11} color={colors.white}>
          {Math.round(percentage * 100)}%
        </OEText>
        <View
          style={[
            styles.triangle,
            bc(colors.transparent),
            borderColor({
              borderLeftColor: colors.transparent,
              borderRightColor: colors.transparent,
              borderTopColor: colors.primary,
            }),
          ]}
        />
      </Animated.View>
    </PopupContainer>
  );
};

const PopupContainer = styled.View`
  position: absolute;
  z-index: 9;
  top: -30px;
`;

const styles = StyleSheet.create({
  popup: {
    paddingVertical: 1,
    borderRadius: 4,
    position: 'absolute',
    top: 0,
    left: 0,
    width: 36,
    zIndex: 9,
  },
  triangle: {
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderTopWidth: 6,
    position: 'absolute',
    bottom: -6,
  },
});
